import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { AttributeDef } from '@/designer/services/configObjectApi'
import type { ListFormFilterField } from '@/designer/types/form.types'
import { RefFieldWidget } from '@/runtime/RefFieldWidget'
import type { CompositePrimitiveType } from '@/runtime/CompositeFieldWidget'
import type { CompositeFilterMeta } from './types'

const COMPOSITE_TARGET_ATTR_BASE = {
  synonym: '',
  required: false,
  indexed: false,
  unique: false,
  hint: '',
  is_standard: false,
  order: 0,
} as const

function CompositeFilterRefValue({
  connectionId,
  target,
  value,
  onChange,
}: {
  connectionId: string
  target: string
  value: string
  onChange: (id: string | null) => void
}): React.JSX.Element {
  const attr: AttributeDef = useMemo(
    () => ({ name: target, attr_type: 'ref', props: { target }, ...COMPOSITE_TARGET_ATTR_BASE }),
    [target],
  )
  return (
    <Box sx={{ minWidth: 160 }} data-testid="composite-filter-value">
      <RefFieldWidget
        attr={attr}
        value={value || null}
        onChange={onChange}
        disabled={false}
        connectionId={connectionId}
        isRequired={false}
      />
    </Box>
  )
}

function CompositeFilterValueText({
  value,
  isNumber,
  onCommit,
}: {
  value: string
  isNumber: boolean
  onCommit: (value: string) => void
}): React.JSX.Element {
  const [text, setText] = useState(value)
  useEffect(() => {
    setText(value)
  }, [value])
  return (
    <TextField
      size="small"
      placeholder="Value"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        if (isNumber) {
          const n = Number(text)
          if (!Number.isFinite(n)) return
          onCommit(String(n))
        } else {
          onCommit(text)
        }
      }}
      sx={{ minWidth: 120, '& .MuiInputBase-root': { fontSize: 12 } }}
    />
  )
}

export interface FiltersPaneProps {
  fields: ListFormFilterField[]
  values: Record<string, string>
  optionsByField: Record<string, Array<{ value: string; label: string }>>
  compositeMeta: Record<string, CompositeFilterMeta>
  connectionId: string
  onChange: (next: Record<string, string>) => void
}

export function FiltersPane({
  fields,
  values,
  optionsByField,
  compositeMeta,
  connectionId,
  onChange,
}: FiltersPaneProps) {
  if (fields.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No filter fields
      </Typography>
    )
  }

  const setField = (name: string, next: string, clearPaired?: string) => {
    const out = { ...values, [name]: next }
    if (clearPaired) delete out[clearPaired]
    onChange(out)
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
      {fields.map((ff) => {
        const composite = compositeMeta[ff.name]
        const currentType = values[ff.name] ?? ''
        return (
          <Box key={ff.name} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              select
              size="small"
              label={ff.title}
              value={currentType}
              onChange={(e) => {
                const next = e.target.value
                setField(ff.name, next, composite?.baseName)
              }}
              sx={{ minWidth: 140, '& .MuiInputBase-root': { fontSize: 12 } }}
            >
              <MenuItem value="">All</MenuItem>
              {ff.options_endpoint
                ? (optionsByField[ff.name] ?? []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))
                : ff.options?.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
            </TextField>
            {composite && composite.refTargets.includes(currentType) && (
              <CompositeFilterRefValue
                connectionId={connectionId}
                target={currentType}
                value={values[composite.baseName] ?? ''}
                onChange={(id) => {
                  const out = { ...values }
                  if (id) out[composite.baseName] = id
                  else delete out[composite.baseName]
                  onChange(out)
                }}
              />
            )}
            {composite && composite.primitives.includes(currentType as CompositePrimitiveType) && (
              <CompositeFilterValueText
                value={values[composite.baseName] ?? ''}
                isNumber={currentType === 'number'}
                onCommit={(text) => onChange({ ...values, [composite.baseName]: text })}
              />
            )}
          </Box>
        )
      })}
    </Box>
  )
}
